import { reaction } from 'mobx';

import { Destructor } from '@models/utility.types';

import { authStore } from './authStore';

/** Utility for triggering a callback hook on authStore isAuthenticated change */
export const createOnAuthenticateListener = (
  /** Callback that's triggered when session credentials are validated.
   * Optionally, returns a cleaning function that's invoked on auth state
   * change */
  onAuthenticate?: () => Destructor | void,
  /** Callback that's session credentials are cleared (on log-out).
   * Optionally, returns a cleaning function that's invoked on auth
   * state change */
  onSignout?: () => Destructor | void
) => {
  const latestStateRef = { status: authStore.status };
  let cleanFn: Destructor | undefined;

  const onAuthChange = (status: typeof authStore.status) => {
    // invoke the correct hook
    const destructor =
      status === 'authenticated'
        ? onAuthenticate?.()
        : status === 'logging-out'
          ? onSignout?.()
          : undefined;
    // set cleanFn from invoked hook
    cleanFn = destructor ?? undefined;
  };

  // invoke callbacks once on listener initialization since handlers
  // are triggered only on store changes and not init by default
  if (authStore.isAuthenticated) onAuthChange(authStore.status);

  return reaction(
    () => authStore.status,
    (status) => {
      // trigger only if state actually changed
      if (status === latestStateRef.status) return;

      latestStateRef.status = status;

      // clean previously invoked callback
      cleanFn?.();

      // invoke callback on authentication state change
      onAuthChange(status);
    }
  );
};
